import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import "../assets/css/Cases.css"; 
import axios from "axios";
import { useAuth } from "../context/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaHistory,
} from "react-icons/fa";
import { RiFolderHistoryFill } from "react-icons/ri";
import { MdAddCircle } from "react-icons/md";
import AddHistory from "../components/AddHistory";
import HistoryViewer from "../components/ViewHistory";
import FileViewerModal from "../components/FileViewerModal";
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{title}</h3>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={onConfirm} className="btn btn-danger">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const Cases = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);

  const [searchCaseNumber, setSearchCaseNumber] = useState("");
  const [searchPetitioner, setSearchPetitioner] = useState("");
  const [searchRespondent, setSearchRespondent] = useState("");
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");
  const [filteredCases, setFilteredCases] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [casesPerPage] = useState(5);

  const [viewerModal, setViewerModal] = useState({
    isOpen: false,
    fileUrl: "",
    fileType: "",
    fileName: ""
  });

  // Move fetchCases outside useEffect so it can be used as a prop
  const fetchCases = async () => {
    if (!user?.token) {
      setError("You must be logged in to view cases.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/api/cases`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data.cases)
      ) {
        setCases(response.data.data.cases);
        setFilteredCases(response.data.data.cases); 
      } else {
        throw new Error("Invalid data format received from server.");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch cases.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch cases from the API
  useEffect(() => {
    fetchCases();
  }, [user, BASE_URL]);

  useEffect(() => {
    const result = cases.filter((c) => {
      const matchesCaseNumber = c.caseNumber.toLowerCase().includes(searchCaseNumber.toLowerCase());
      const matchesPetitioner = c.petitionerName.toLowerCase().includes(searchPetitioner.toLowerCase());
      const matchesRespondent = c.respondentName.toLowerCase().includes(searchRespondent.toLowerCase());
      let matchesDate = true;
      if (searchStartDate) {
        matchesDate = matchesDate && new Date(c.date) >= new Date(searchStartDate);
      }
      if (searchEndDate) {
        matchesDate = matchesDate && new Date(c.date) <= new Date(searchEndDate);
      }
      return matchesCaseNumber && matchesPetitioner && matchesRespondent && matchesDate;
    });
    setFilteredCases(result);
    setCurrentPage(1); 
  }, [searchCaseNumber, searchPetitioner, searchRespondent, searchStartDate, searchEndDate, cases]);

  const handleDeleteClick = (caseId, caseNumber) => {
    if (!hasPermission("delete_cases")) {
      toast.error("You don't have permission to delete cases");
      return;
    }
    setCaseToDelete({ id: caseId, number: caseNumber });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!caseToDelete) return;

    const deletePromise = new Promise(async (resolve, reject) => {
      try {
        setIsDeleting(true);
        const response = await axios.delete(
          `${BASE_URL}/api/cases/${caseToDelete.id}`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );

        if (response.data?.status === "success") {
          // Update both original and filtered lists
          setCases(cases.filter((c) => c._id !== caseToDelete.id));
          resolve(`Case #${caseToDelete.number} deleted successfully`);
        } else {
          reject(new Error(response.data?.message || "Failed to delete case"));
        }
      } catch (err) {
        reject(
          new Error(
            err.response?.data?.message ||
              err.message ||
              "Failed to delete case"
          )
        );
      } finally {
        setIsDeleting(false);
        setShowDeleteModal(false);
        setCaseToDelete(null);
      }
    });

    toast.promise(deletePromise, {
      loading: "Deleting case...",
      success: (message) => message,
      error: (err) => err.message,
    });
  };

  const indexOfLastCase = currentPage * casesPerPage;
  const indexOfFirstCase = indexOfLastCase - casesPerPage;
  const currentCases = filteredCases.slice(indexOfFirstCase, indexOfLastCase);
  const totalPages = Math.ceil(filteredCases.length / casesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFileDownload = async (caseId, fileId, filename) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/cases/${caseId}/files/${fileId}?download=true`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          responseType: "blob",
        }
      );

      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("File downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const handleFileView = async (caseId, fileId, filename, mimetype) => {
    try {
      // For PDFs and images, show in modal
      if (mimetype === "application/pdf" || mimetype.startsWith("image/")) {
        // Create a blob URL with authorization
        const response = await axios.get(
          `${BASE_URL}/api/cases/${caseId}/files/${fileId}`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
            responseType: 'blob'
          }
        );

        const blob = new Blob([response.data], { type: mimetype });
        const fileUrl = URL.createObjectURL(blob);

        setViewerModal({
          isOpen: true,
          fileUrl: fileUrl,
          fileType: mimetype,
          fileName: filename
        });
      } else {
        // For other files, trigger download
        handleFileDownload(caseId, fileId, filename);
      }
    } catch (error) {
      console.error("View error:", error);
      toast.error("Failed to open file");
    }
  };

  // Add cleanup for blob URLs
  useEffect(() => {
    return () => {
      // Cleanup blob URLs when component unmounts
      if (viewerModal.fileUrl && viewerModal.fileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(viewerModal.fileUrl);
      }
    };
  }, [viewerModal.fileUrl]);

  // Render Loading State
  if (loading) {
    return (
      <Layout>
        <div className="cases-container">
          <div className="loading-container">Loading cases...</div>
        </div>
      </Layout>
    );
  }

  // Render Error State
  if (error) {
    return (
      <Layout>
        <div className="cases-container">
          <div className="error-container">Error: {error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCaseToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete case #${caseToDelete?.number}? This action cannot be undone.`}
      />
      <FileViewerModal
        isOpen={viewerModal.isOpen}
        onClose={() => setViewerModal({ ...viewerModal, isOpen: false })}
        fileUrl={viewerModal.fileUrl}
        fileType={viewerModal.fileType}
        fileName={viewerModal.fileName}
      />
      <div className="cases-container">
        <div className="page-header">
          <h1>Case List</h1>
          {hasPermission("add_cases") && (
            <Link to="/add-case" className="btn btn-primary">
              Add New Case
            </Link>
          )}
        </div>

        {/* --- NEW: Search input fields --- */}
        <div className="search-controls">
          <input
            type="text"
            placeholder="Search by Case Number"
            className="search-input"
            value={searchCaseNumber}
            onChange={(e) => setSearchCaseNumber(e.target.value)}
          />
          <input
            type="text"
            placeholder="Search by Petitioner"
            className="search-input"
            value={searchPetitioner}
            onChange={(e) => setSearchPetitioner(e.target.value)}
          />
          <input
            type="text"
            placeholder="Search by Respondent"
            className="search-input"
            value={searchRespondent}
            onChange={(e) => setSearchRespondent(e.target.value)}
          />
          <div className="search-group">
            
            <label htmlFor="date-start" style={{marginRight: 4}}>Start Date:</label>
            <input
              type="date"
              placeholder="Start date"
              className="search-input"
              value={searchStartDate}
              onChange={(e) => setSearchStartDate(e.target.value)}
            />
            <label htmlFor="date-end" style={{marginRight: 4, marginLeft: 4}}>End Date:</label>
            <input
              type="date"
              placeholder="End date"
              className="search-input"
              value={searchEndDate}
              onChange={(e) => setSearchEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="table-responsive">
          <table className="cases-table">
            <thead>
              <tr>
                <th>Case No</th>
                <th>Case Type</th>
                <th>Date</th>
                <th>Year</th>
                <th>Grouping</th>
                <th>Petitioner</th>
                <th>Respondent</th>
                <th>Files</th>
                <th>Current Status</th>
                <th>Actions</th>
                <th>History</th>
              </tr>
            </thead>
            <tbody>
              {currentCases.length > 0 ? (
                currentCases.map((singleCase) => (
                  <tr key={singleCase._id}>
                    <td>
                    <div className="case-number">
                          {singleCase.caseNumber}
                        </div>
                    </td>
                    <td>
                      <div className="case-info">
                        <div className="case-type">{singleCase.caseType}</div>
                       
                      </div>
                    </td>
                    
                    <td>{singleCase.date ? new Date(singleCase.date).toLocaleDateString('en-GB') : ""}</td>
                    <td>{singleCase.year}</td>
                    <td>{singleCase.grouping}</td>
                    <td>
                      <div className="party-details">
                        <div className="party-name">
                          {singleCase.petitionerName}
                        </div>
                        <div className="advocate-name">
                          Adv: {singleCase.petitionerAdvocate}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="party-details">
                        <div className="party-name">
                          {singleCase.respondentName}
                        </div>
                        <div className="advocate-name">
                          Adv: {singleCase.respondentAdvocate}
                        </div>
                      </div>
                    </td>
                    
                    <td>
                      <div className="files-list">
                        {singleCase.files && singleCase.files.length > 0 ? (
                          singleCase.files.map((file) => (
                            <div key={file._id} className="file-item">
                              <span>{file.filename}</span>
                              <div className="file-actions">
                                {(file.mimetype === "application/pdf" ||
                                  file.mimetype.startsWith("image/")) && (
                                  <button
                                    onClick={() =>
                                      handleFileView(
                                        singleCase._id,
                                        file._id,
                                        file.filename,
                                        file.mimetype
                                      )
                                    }
                                    className="action-btn view-file-btn"
                                    title={`View ${file.filename}`}
                                  >
                                    <FaEye />
                                  </button>
                                )}
                                {/* Download Icon - Show for all files */}
                                <button
                                  onClick={() =>
                                    handleFileDownload(
                                      singleCase._id,
                                      file._id,
                                      file.filename
                                    )
                                  }
                                  className="action-btn download-file-btn"
                                  title={`Download ${file.filename}`}
                                >
                                  <FaDownload />
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <span>No files</span>
                        )}
                      </div>
                    </td>
                    <td>{singleCase.status}</td>
                    <td>
                      <div className="actions-cell">
                        <Link
                          to={`/cases/${singleCase._id}`}
                          className="action-btn view-btn"
                          title="View Details"
                        >
                          <FaEye />
                        </Link>
                        {hasPermission("edit_cases") && (
                          <Link
                            to={`/cases/edit/${singleCase._id}`}
                            className="action-btn edit-btn"
                            title="Edit"
                          >
                            <FaEdit />
                          </Link>
                        )}
                        {hasPermission("delete_cases") && (
                          <button
                            onClick={() =>
                              handleDeleteClick(
                                singleCase._id,
                                singleCase.caseNumber
                              )
                            }
                            className="action-btn delete-btn"
                            title="Delete"
                            disabled={isDeleting}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`/case-history/${singleCase._id}`}
                          className="action-btn view-btn"
                          title="View Details"
                        >
                           <FaHistory/>
                        </Link>
                        {hasPermission("edit_cases") && (
                          <button className="action-btn edit-btn" title="Edit">
                            <AddHistory id={singleCase._id} onHistoryAdded={fetchCases} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No cases found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaChevronLeft /> Prev
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next <FaChevronRight />
            </button>
          </div>
        )}
      </div>
      
    </Layout>
  );
};

export default Cases;
